import React, { useState, useEffect } from "react";
import { Button, Spinner } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useDropzone } from "react-dropzone";
import { ToastAlert } from "../../components/ToastAlert"; // 👈 your toast system
import API from "../../utils/api";

const MandatePage = () => {
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [mandateFile, setMandateFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [userId, setUserId] = useState(null);

  // Toast
  const [alert, setAlert] = useState({
    show: false,
    message: "",
    variant: "info",
  });

  const showAlert = (message, variant = "info") => {
    setAlert({ show: true, message, variant });
  };

  // ------------------------
  // Get current user
  // ------------------------
  const fetchCurrentUser = async () => {
    try {
      const res = await API.get("/auth/me");
      setUserId(res?.data?.user?._id || null);
    } catch (err) {
      showAlert("Failed to fetch user", "danger");
    }
  };

  // ------------------------
  // Fetch mandate
  // ------------------------
  const fetchMandate = async (uid) => {
    if (!uid) return;
    setLoading(true);
    try {
      const res = await API.get(`claims/mandate/${uid}`);
      setMandateFile(res.data.mandateFile || null);
    } catch (err) {
      showAlert("Failed to load mandate", "danger");
    }
    setLoading(false);
  };

  useEffect(() => { fetchCurrentUser(); }, []);
  useEffect(() => { if (userId) fetchMandate(userId); }, [userId]);

  // ------------------------
  // Dropzone
  // ------------------------
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "application/pdf": [] },
    multiple: false,
    onDrop: (files) => setFile(files[0]),
  });

  // ------------------------
  // Upload mandate
  // ------------------------
  const handleUpload = async (e) => {
    e.stopPropagation(); // ❗ prevent file picker from opening again

    if (!file || !userId) return;
    setUploading(true);

    const formData = new FormData();
    formData.append("mandate", file);

    try {
      const res = await API.post(`claims/mandate/${userId}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setMandateFile(res.data.mandateFile);
      setFile(null);

      showAlert("Mandate uploaded successfully!", "success");
    } catch (err) {
      showAlert("Failed to upload mandate", "danger");
    }

    setUploading(false);
  };

  // ------------------------
  // Delete mandate
  // ------------------------
  const handleDelete = async () => {
    if (!window.confirm("Delete this mandate?")) return; // optional popup

    setUploading(true);
    try {
      await API.delete(`claims/mandate/${userId}`);
      setMandateFile(null);
      showAlert("Mandate deleted successfully!", "success");
    } catch (err) {
      showAlert("Error deleting mandate", "danger");
    }
    setUploading(false);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "650px",
        margin: "50px auto",
        padding: "25px",
        borderRadius: "14px",
        backgroundColor: "#ffffff",
        boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
        marginTop: "120px",

      }}
    >
      <ToastAlert
        show={alert.show}
        onClose={() => setAlert({ ...alert, show: false })}
        message={alert.message}
        variant={alert.variant}
      />

      {/* Page Title */}


      {/* Uploaded Mandate */}
      {mandateFile ? (
        <>
          <h4
            style={{
              textAlign: "center",
              marginBottom: "25px",
              fontWeight: "600",
              color: "#374151",
            }}
          >
            {t("UploadMandateHeading")}
          </h4>
          <div
            style={{
              padding: "15px",
              borderRadius: "10px",
              backgroundColor: "#f0f4ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
            }}
          >
            <a
              href={`https://api-dashboard.nordmann-inkasso.de/${mandateFile.replace(/\\/g, "/")}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                color: "#4f46e5",
                fontWeight: "500",
              }}
            >
              <FontAwesomeIcon icon={faFilePdf} size="lg" />
              {t("UploadedMandate")}
            </a>

            <Button size="sm" variant="outline-danger" onClick={handleDelete} disabled={uploading}>
              <FontAwesomeIcon icon={faTrash} /> {t("Delete")}
            </Button>
          </div>
        </>

      ) : (
        <>
          {/* Dropzone Box */}
          <h4
            style={{
              textAlign: "center",
              marginBottom: "25px",
              fontWeight: "600",
              color: "#374151",
            }}
          >
            {t("UploadedMandateHeading")}
          </h4>
          <div
            {...getRootProps()}
            style={{
              border: "2px dashed #4f46e5",
              padding: "40px",
              textAlign: "center",
              cursor: "pointer",
              borderRadius: "12px",
              backgroundColor: isDragActive ? "#eef2ff" : "#fafafa",
              transition: "all 0.2s ease-in-out",
              position: "relative",
            }}
          >
            <input {...getInputProps()} />

            <FontAwesomeIcon icon={faUpload} size="2x" color="#4f46e5" />

            <p style={{ marginTop: "10px", color: "#6b7280" }}>
              {isDragActive ? "Drop the file…" : "Drag & drop PDF or click to select"}
            </p>

            {/* Show File Preview */}
            {file && (
              <div
                style={{
                  marginTop: "15px",
                  padding: "10px 15px",
                  borderRadius: "8px",
                  backgroundColor: "#fff",
                  border: "1px solid #4f46e5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <FontAwesomeIcon icon={faFilePdf} color="#e11d48" />
                  <span>{file.name}</span>
                </div>

                <span
                  style={{ cursor: "pointer", fontWeight: "bold", color: "#6b7280" }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setFile(null);
                  }}
                >
                  ✕
                </span>
              </div>
            )}
          </div>

          {/* Upload button OUTSIDE dropzone, so no re-trigger */}
          {file && (
            <Button
              size="md"
              className="mt-3 w-100"
              style={{ backgroundColor: "#4f46e5", borderColor: "#4f46e5" }}
              onClick={handleUpload}
              disabled={uploading}
            >
              {uploading ? t("UploadingMandate") : t("UploadMandate")}
            </Button>
          )}
        </>
      )}
    </div>
  );
};

export default MandatePage;
